/**
 * useShellAreaTargeting - who an area attack catches, answered from the map.
 *
 * `useAttackExecution` has never seen the battlefield and should not start:
 * the geometry is pure and lives in `engine/battlefield/area.ts`, and the
 * shell is the one place that holds both the map and the roster. So the
 * attack hook asks, and this answers with a list of victims, each carrying
 * THEIR OWN projection - their cover, from this attacker - and their own
 * range band. Everything downstream then resolves per victim by construction.
 *
 * Returns three callbacks:
 * - `suppressionZone(slot, primaryTabId)` - ids in Suppressing Fire's arc.
 * - `resolveVictims(slot, primaryTabId, scatter, rolled)` - null for an attack
 *   that is not an area one (or with no map to ask), else the victims. An
 *   empty list is a different answer: the template landed on nobody.
 * - `resolveSpawned(slot, spawnerIds, spawn, struckIds)` - the secondary
 *   template's victims (v8.373.2), or null with nobody on the map.
 *
 * Every callback reads the measuring tape (`measureLineRef`) and the ground
 * (`surfaceEffectsRef`) at call time, so neither re-renders the world.
 * Extracted from DeathWatchSimulator; the maths is unchanged.
 */
import { sideOf } from '../engine/roster/team';
import { useCallback, useMemo } from 'react';
import { sprayLengthOf, DEFAULT_BEAM_WIDTH_M } from '../engine/areaShapes';
import { SUPPRESSION_CONE_DEGREES } from '../engine/attackCalc';
import { combatantsInCircle, combatantsInCone, combatantsAlongBeam, arcChain } from '../engine/battlefield/area';
import { selectSpawned } from '../engine/battlefield/spawnedArea';
import { bearingBetween } from '../engine/battlefield/facing';
import { relateFrom } from '../engine/battlefield/relations';
import { effectReaches } from '../engine/effectScope';
import { hordeMagnitudes } from '../engine/roster/hordes';
import { projectDefenderFrom } from '../engine/roster/projection';
import { blastCatchesAttacker } from '../engine/blastRules';

/** Stable identity: a new [] each render would defeat every memo below it. */
export const EMPTY_SOLIDS = [];

/** The tokens that still belong to somebody on the roster. */
export function rosterTokens(tokens, roster) {
  return Object.values(tokens).filter((t) => roster.some((c) => c.id === t.id));
}

/** A combatant's side (2026-10-23: allied teams share one), or null for nobody / no team. */
export function sideTagOf(combatant) {
  return sideOf(combatant?.sheet?.team);
}

/**
 * WHO A TEMPLATE MAY CATCH: undefined (anyone) or a predicate over ids -
 * the block's `catches`, by the attacker's side, strict. `aimedId`, when
 * given, is always caught: the one aimed at is in it by definition.
 */
export function catchPredicate(catches, attackerSide, roster, aimedId = null) {
  if (catches === 'anyone') return undefined;
  return (id) => id === aimedId || effectReaches(catches, attackerSide, sideTagOf(roster.find((c) => c.id === id)));
}

/** Where the tape ends, else whoever was aimed at - a ground point, or null. */
export function aimPoint(measured, aimed) {
  if (measured) return { x: measured.x, z: measured.z };
  return aimed ? { x: aimed.x, z: aimed.z } : null;
}

/** The same point with its height, for a template centred on it. */
function aimCentre(measured, aimed) {
  if (measured) return { x: measured.x, z: measured.z, elevation: measured.token?.elevation ?? 0 };
  return aimed ? { x: aimed.x, z: aimed.z, elevation: aimed.elevation } : null;
}

/**
 * WHERE A SCATTERED THROW ACTUALLY LANDED. The engine rolled the clock face
 * and the distance; placing it needs the map. One o'clock is straight on
 * past the target - the natural overthrow - so the offset is measured from
 * the line the throw was travelling along.
 */
export function scatterCentre(aimedAt, shooterToken, scatter) {
  const throwLine = shooterToken ? bearingBetween(shooterToken, aimedAt) : 0;
  const away = ((throwLine + scatter.offsetDegrees) * Math.PI) / 180;
  return {
    x: aimedAt.x + Math.cos(away) * scatter.metres,
    z: aimedAt.z + Math.sin(away) * scatter.metres,
    elevation: aimedAt.elevation,
  };
}

/** The shape this shot rolled (v8.372.4), as the four questions asked of it. */
export function rolledShape(rolled) {
  return {
    radius: rolled?.shape === 'blast' ? rolled.radius : 0,
    coneDegrees: rolled?.shape === 'cone' ? rolled.angle : 0,
    beamActive: rolled?.shape === 'beam',
    arcActive: rolled?.shape === 'arc' && rolled.reach > 0 && rolled.leaps > 0,
  };
}

/** Suppression makes ANY full-auto weapon an area attack for one shot. */
export function isAreaShot(shape, suppressing) {
  const sized = shape.radius > 0 || shape.coneDegrees > 0;
  return suppressing || sized || shape.arcActive || shape.beamActive;
}

/** The shooter's hand, as the shapes want an origin. */
function originOf(token) {
  return { x: token.x, z: token.z, elevation: token.elevation };
}

/** The shooter's own token, or null when acting as nobody or off the map. */
function shooterOf(field) {
  return field.actorId ? field.tokens[field.actorId] : null;
}

/** "Never the shooter" - BY ID, so someone on the same spot is still a victim. */
function excludeShooter(field) {
  return field.actorId ? [field.actorId] : undefined;
}

/**
 * WHO IS IN THE KILL ZONE (v8.307.0) - a 45-degree arc from the shooter, out
 * to the weapon's reach, along the tape if one is drawn and otherwise at the
 * target. ONE derivation for who gets shot and who has to keep their head
 * down: the two run at different moments, and must name the same people.
 */
export function suppressionZoneOf(field, slot, primaryTabId) {
  const shooterToken = shooterOf(field);
  if (!shooterToken) return [];
  const tokenList = rosterTokens(field.tokens, field.roster);
  if (!tokenList.length) return [];
  const at = aimPoint(field.measured, field.tokens[primaryTabId]);
  if (!at) return [];
  return combatantsInCone(
    originOf(shooterToken),
    bearingBetween(shooterToken, at),
    slot?.range || 30,
    SUPPRESSION_CONE_DEGREES,
    tokenList,
    {
      solids: field.solids,
      includeHidden: field.isGm,
      hordeMagnitude: field.hordeMagnitude,
      exclude: excludeShooter(field),
    },
  );
}

/** One victim, with their own projection from this attacker. */
function victimOf(field, id, relation) {
  const combatant = field.roster.find((c) => c.id === id);
  if (!combatant) return { tabId: id, rangeBand: relation?.rangeBand };
  return {
    tabId: id,
    target: projectDefenderFrom(
      combatant,
      field.derived ? relation?.cover : null,
      field.surfaceEffects[id] ?? null,
      field.coverConflictMode,
      null,
    ),
    rangeBand: relation?.rangeBand,
  };
}

/**
 * Ids into victims. Cover and range band are facts about the PAIR, so every
 * victim is measured from the attacker separately - `relateFrom` is that,
 * for a list, in one call. Shared by every shape and by BOTH templates.
 */
export function projectVictimList(field, slot, ids) {
  if (!ids.length) return [];
  const shooterToken = shooterOf(field);
  const caughtSet = new Set(ids);
  const caughtTokens = rosterTokens(field.tokens, field.roster).filter((t) => caughtSet.has(t.id));
  const relations = shooterToken
    ? relateFrom(shooterToken, caughtTokens, field.solids, {
        weaponRange: slot?.range,
        includeHidden: field.isGm,
        arcPreset: field.arcPreset,
        rules: field.relationRules,
      })
    : new Map();
  return ids.map((id) => victimOf(field, id, relations.get(id)));
}

/**
 * WHO THE SECONDARY TEMPLATE CATCHES (v8.373.2) - the secondary's own
 * `catches`, judged against the ATTACKER's team and strict: a spawned copy
 * is aimed at nobody, so nobody is exempt.
 */
function spawnedVictims(field, { slot, spawnerIds, spawn, struckIds }) {
  const tokenList = rosterTokens(field.tokens, field.roster);
  if (!tokenList.length) return null;
  const caught = selectSpawned(spawnerIds, shooterOf(field) || null, tokenList, spawn, {
    struck: field.actorId ? [...struckIds, field.actorId] : struckIds,
    solids: field.solids,
    includeHidden: field.isGm,
    hordeMagnitude: field.hordeMagnitude,
    eligible: catchPredicate(spawn.catches, field.attackerTeam, field.roster),
    sightPerLeap: spawn.sightPerLeap,
  });
  const by = new Map(caught.map((v) => [v.id, v]));
  return field
    .projectVictims(
      slot,
      caught.map((v) => v.id),
    )
    .map((v) => {
      const { leap, spawnerId } = by.get(v.tabId);
      const burst = copyBurst(field.tokens[spawnerId], spawn, leap);
      return { ...v, leap, spawnedFrom: spawnerId, ...(burst ? { area: burst } : {}) };
    });
}

/** A spawned BLAST copy's template, centred on its spawner, set off after the primary (T2); none for other shapes. */
export function copyBurst(spawner, spawn, leap) {
  if (spawn.shape !== 'blast' || !(spawn.radius > 0) || !spawner) return null;
  return { x: spawner.x, z: spawner.z, r: spawn.radius, copy: Math.max(1, leap || 1) };
}

/**
 * THE ARC (v8.372.4): from the aimed victim, leap by leap, to the nearest
 * untouched eligible combatant within reach of the LAST one - the hook
 * scales each echo by its leap. The struck target is the origin in melee too.
 */
function arcVictims(shot) {
  const primaryToken = shot.field.tokens[shot.primaryTabId];
  if (!primaryToken) return null;
  const chain = arcChain(primaryToken, shot.tokenList, shot.rolled.reach, shot.rolled.leaps, {
    ...shot.options,
    sightPerLeap: shot.rolled.sightPerLeap,
  });
  const leapOf = new Map(chain.map((c) => [c.id, c.leap]));
  return shot.victimsFrom(chain.map((c) => c.id)).map((v) => ({ ...v, leap: leapOf.get(v.tabId) ?? 0 }));
}

/** The beam is a lance from the muzzle (v8.292.0): everyone on the line. */
function beamVictims(shot) {
  if (!shot.shooterToken) return null;
  const caught = combatantsAlongBeam(
    originOf(shot.shooterToken),
    bearingBetween(shot.shooterToken, shot.centre),
    shot.rolled.length || sprayLengthOf(shot.slot),
    shot.tokenList,
    shot.options,
    (shot.rolled.width || DEFAULT_BEAM_WIDTH_M) / 2,
  );
  return shot.victimsFrom(caught);
}

/**
 * A HAND HOLDS THE FLAMER, so a cone starts at the attacker and needs one.
 * It reaches as far as the block says, else as far as the weapon does.
 */
function coneVictims(shot) {
  if (!shot.shooterToken) return null;
  const caught = combatantsInCone(
    originOf(shot.shooterToken),
    bearingBetween(shot.shooterToken, shot.centre),
    shot.rolled.length || sprayLengthOf(shot.slot),
    shot.shape.coneDegrees,
    shot.tokenList,
    shot.options,
  );
  return shot.victimsFrom(caught);
}

/**
 * THE THROWER IS NOT IN THE SHAPE THEY AIMED. A melee blast is centred on
 * somebody you are standing next to (user, 2026-08-17); a grenade that
 * scatters back onto them is the exception the table rules on - see
 * engine/blastRules.ts.
 */
function blastVictims(shot) {
  const { field } = shot;
  const isMelee = shot.slot?.weaponType === 'melee';
  const caught = combatantsInCircle(shot.centre, shot.shape.radius, shot.tokenList, {
    ...shot.options,
    exclude:
      field.actorId && (isMelee || !blastCatchesAttacker(field.blastSelfPolicy, !!shot.scatter))
        ? [field.actorId]
        : undefined,
  });
  return shot.victimsFrom(caught).map((v) => ({ ...v, area: blastBurst(shot) }));
}

/**
 * The template for the display's detonation (plan design-gaps, T2): where it went off, how big, and where the throw
 * was aimed when it scattered there. Spread only when there is one: the beat's column, and the defence write that
 * carries it, refuse `undefined` (gotcha #2).
 */
function blastBurst(shot) {
  const at = { x: shot.centre.x, z: shot.centre.z, r: shot.shape.radius };
  return shot.scatter ? { ...at, from: { x: shot.aimedAt.x, z: shot.aimedAt.z } } : at;
}

/**
 * The shape decides WHO. Suppressing Fire's arc outranks the weapon's own
 * shape - you are spraying, not aiming - and comes from the SAME function
 * the pinning sweep uses, so the two cannot disagree about who was in it.
 */
function victimsByShape(shot) {
  if (shot.shape.arcActive) return arcVictims(shot);
  if (shot.suppressing) return shot.victimsFrom(shot.field.suppressionZone(shot.slot, shot.primaryTabId));
  if (shot.shape.beamActive) return beamVictims(shot);
  if (shot.shape.coneDegrees > 0) return coneVictims(shot);
  return blastVictims(shot);
}

/** The options every shape shares: sight, hordes, eligibility, not the shooter. */
function shapeOptions(field, eligible) {
  return {
    solids: field.solids,
    includeHidden: field.isGm,
    hordeMagnitude: field.hordeMagnitude,
    exclude: excludeShooter(field),
    eligible,
  };
}

/**
 * Everyone this area shot catches (see the module header). The CENTRE is
 * where you pointed, or failing that whoever you aimed at - a place rather
 * than a person, since half the point of a blast is dropping it between two.
 */
export function areaVictims(field, { slot, primaryTabId, scatter, rolled }) {
  const shape = rolledShape(rolled);
  const suppressing = field.attackMode === 'suppressing';
  if (!isAreaShot(shape, suppressing)) return null;
  const eligible = rolled ? catchPredicate(rolled.catches, field.attackerTeam, field.roster, primaryTabId) : undefined;
  const tokenList = rosterTokens(field.tokens, field.roster);
  if (!tokenList.length) return null;
  const aimedAt = aimCentre(field.measured, field.tokens[primaryTabId]);
  if (!aimedAt) return null;
  const shooterToken = shooterOf(field);
  const centre = scatter ? scatterCentre(aimedAt, shooterToken, scatter) : aimedAt;
  const options = shapeOptions(field, eligible);
  const victimsFrom = (ids) => field.projectVictims(slot, ids);
  const shot = { field, slot, primaryTabId, scatter, rolled, shape, suppressing, tokenList };
  return victimsByShape({ ...shot, shooterToken, aimedAt, centre, options, victimsFrom });
}

/**
 * What every question asks of the map, as one object whose identity moves
 * only when one of them does - so the callbacks below rebuild exactly then.
 */
function useField({ tokens, roster, actor, actorId, ignoreTerrain, mapSolids, isGm, relationRules }) {
  const solids = ignoreTerrain ? EMPTY_SOLIDS : mapSolids;
  const attackerTeam = sideTagOf(actor);
  // A horde's bulk in a template: the shell's record holds it already (one lookup per roster, not per call).
  const hordeMagnitude = useMemo(
    () => relationRules?.hordeMagnitude ?? hordeMagnitudes(roster ?? []),
    [relationRules, roster],
  );
  return useMemo(
    () => ({ tokens, roster, actorId, solids, isGm, attackerTeam, hordeMagnitude }),
    [tokens, roster, actorId, solids, isGm, attackerTeam, hordeMagnitude],
  );
}

/** The victims' projection callback; the ground is read at call time. */
function useProjectVictims(field, { arcPreset, derived, coverConflictMode, relationRules, surfaceEffectsRef }) {
  return useCallback(
    (slot, ids) =>
      projectVictimList(
        { ...field, arcPreset, derived, coverConflictMode, relationRules, surfaceEffects: surfaceEffectsRef.current },
        slot,
        ids,
      ),
    [field, arcPreset, derived, coverConflictMode, relationRules, surfaceEffectsRef],
  );
}

/** The whole area answer; the tape is read at call time. */
function useResolveVictims(field, { blastSelfPolicy, attackMode, measureLineRef }, projectVictims, suppressionZone) {
  return useCallback(
    (slot, primaryTabId, scatter, rolled) =>
      areaVictims(
        {
          ...field,
          blastSelfPolicy,
          attackMode,
          projectVictims,
          suppressionZone,
          measured: measureLineRef.current?.to,
        },
        { slot, primaryTabId, scatter, rolled },
      ),
    [field, blastSelfPolicy, attackMode, projectVictims, suppressionZone, measureLineRef],
  );
}

/**
 * @param {object} args
 * @param {Record<string, object>} args.tokens - the battlefield's markers, by combatant id
 * @param {object[]} args.roster
 * @param {object|null} args.actor - the acting combatant (for their team)
 * @param {string|null} args.actorId
 * @param {boolean} args.ignoreTerrain - the house rule: terrain blocks nothing
 * @param {object[]} args.mapSolids
 * @param {boolean} args.isGm - a GM's templates see hidden markers
 * @param {string} args.arcPreset - the table's vehicle arc preset
 * @param {boolean} args.derived - may the map's cover reach a victim at all
 * @param {string} args.coverConflictMode
 * @param {object} args.relationRules - the table's rulings a relation reads (`RelationRules`: the cover reach and
 *   each horde's magnitude)
 * @param {string} args.blastSelfPolicy - may a scattered blast catch its thrower
 * @param {string|null} args.attackMode - 'suppressing' for one shot, else null
 * @param {{current: object}} args.surfaceEffectsRef
 * @param {{current: object|null}} args.measureLineRef
 */
export default function useShellAreaTargeting(args) {
  const field = useField(args);
  const { measureLineRef } = args;
  const suppressionZone = useCallback(
    (slot, primaryTabId) => suppressionZoneOf({ ...field, measured: measureLineRef.current?.to }, slot, primaryTabId),
    [field, measureLineRef],
  );
  const projectVictims = useProjectVictims(field, args);
  const resolveSpawned = useCallback(
    (slot, spawnerIds, spawn, struckIds) =>
      spawnedVictims({ ...field, projectVictims }, { slot, spawnerIds, spawn, struckIds }),
    [field, projectVictims],
  );
  const resolveVictims = useResolveVictims(field, args, projectVictims, suppressionZone);
  return { suppressionZone, resolveVictims, resolveSpawned };
}
